import React, { useEffect, useState, useRef } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import Webcam from 'react-webcam';
import API from '../services/api';
import './AttendanceForm.css';

const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}`
    );
    if (!res.ok) return '';
    const data = await res.json();
    return data?.display_name || '';
  } catch {
    return '';
  }
};

const AttendanceForm = () => {
  const [location, setLocation] = useState({ lat: '', lng: '' });
  const [locationAddress, setLocationAddress] = useState('');
  const [employee, setEmployee] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [capturedImage, setCapturedImage] = useState(null);
  const [record, setRecord] = useState(null);
  const [policyInfo, setPolicyInfo] = useState(null);
  const [meetingNote, setMeetingNote] = useState('');
  const [isDesktop, setIsDesktop] = useState(false);
  const webcamRef = useRef(null);
  const trackingWatchRef = useRef(null);
  const activeMode = policyInfo?.attendanceMode === 'field' ? 'field' : 'office';

  const fetchLocation = () =>
    new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setLocation(coords);
          const readable = await reverseGeocode(coords.lat, coords.lng);
          if (readable) setLocationAddress(readable);
          resolve(coords);
        },
        () => {
          setMessage('Please allow location access.');
          resolve(null);
        }
      );
    });

  const fetchBaseData = async () => {
    setLoading(true);
    try {
      const [employeeRes, todayRes, policyRes] = await Promise.all([
        API.get('/employee/me'),
        API.get('/attendance/today'),
        API.get('/attendance/policy'),
      ]);
      setEmployee(employeeRes.data || null);
      setRecord(todayRes.data || null);
      setPolicyInfo(policyRes.data || null);
    } catch (err) {
      console.error('Failed to load attendance data:', err);
      setMessage('Failed to load attendance/profile data. Please login again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBaseData();
    fetchLocation();
  }, []);

  useEffect(() => {
    const shouldTrack = activeMode === 'field' && record?.punchIn?.time && !record?.punchOut?.time;
    if (!shouldTrack || !navigator?.geolocation) return undefined;

    trackingWatchRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setLocation(coords);
        const readable = await reverseGeocode(coords.lat, coords.lng);
        if (readable) setLocationAddress(readable);
        API.post('/attendance/field/location', {
          location: coords,
          address: readable || locationAddress,
          note: 'auto_live_tracking',
        }).catch(() => null);
      },
      () => null,
      {
        enableHighAccuracy: true,
        maximumAge: 30000,
        timeout: 20000,
      }
    );

    return () => {
      if (trackingWatchRef.current != null) {
        navigator.geolocation.clearWatch(trackingWatchRef.current);
        trackingWatchRef.current = null;
      }
    };
  }, [activeMode, record?.punchIn?.time, record?.punchOut?.time, locationAddress]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const handle = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', handle);
      return () => mq.removeEventListener('change', handle);
    }
    mq.addListener(handle);
    return () => mq.removeListener(handle);
  }, []);

  const handleCapture = () => {
    const imageSrc = webcamRef.current?.getScreenshot?.();
    if (imageSrc) setCapturedImage(imageSrc);
  };

  const ensureCurrentLocation = async () => {
    if (location.lat && location.lng) return location;
    return fetchLocation();
  };

  const runFieldAction = async (endpoint, successText) => {
    setMessage('');
    setLoading(true);
    try {
      const coords = await ensureCurrentLocation();
      if (!coords) {
        setMessage('❌ Location not detected. Please enable GPS and refresh.');
        return;
      }
      const res = await API.post(endpoint, {
        location: coords,
        address: locationAddress,
        note: meetingNote,
      });
      setMessage(res.data?.message || successText);
      await fetchBaseData();
    } catch (err) {
      setMessage(err.response?.data?.message || '❌ Request failed.');
    } finally {
      setLoading(false);
    }
  };

  const handlePunchIn = async () => {
    setMessage('');
    if (!capturedImage) {
      setMessage('❌ Please capture a selfie first.');
      return;
    }

    setLoading(true);
    try {
      const coords = await ensureCurrentLocation();
      if (!coords) {
        setMessage('❌ Location not detected. Please enable GPS and refresh.');
        return;
      }

      const res = await API.post('/attendance/punch-in', {
        location: coords,
        address: locationAddress,
        selfie: capturedImage,
      });
      setMessage(res.data?.message || '✅ Punch-in recorded successfully!');
      if (res.data?.record) {
        setRecord(res.data.record);
      } else {
        await fetchBaseData();
      }
    } catch (err) {
      setMessage(err.response?.data?.message || '❌ Failed to punch in.');
    }
    setLoading(false);
  };

  const handlePunchOut = async () => {
    setMessage('');
    setLoading(true);
    try {
      const coords = await ensureCurrentLocation();
      if (!coords) {
        setMessage('❌ Location not detected. Please enable GPS and refresh.');
        return;
      }

      const res = await API.post('/attendance/punch-out', {
        location: coords,
        address: locationAddress,
      });
      setMessage(res.data?.message || '✅ Punch-out recorded successfully!');
      if (res.data?.record) {
        setRecord(res.data.record);
      } else {
        await fetchBaseData();
      }
    } catch (err) {
      setMessage(err.response?.data?.message || '❌ Failed to punch out.');
    }
    setLoading(false);
  };

  const formatStatus = (status) => {
    if (!status) return 'Not marked';
    const s = String(status).toLowerCase();
    if (s === 'halfday' || s === 'half-day') return 'Half Day';
    if (s === 'present') return 'Present';
    if (s === 'outside-office') return 'Outside Office';
    if (s === 'absent') return 'Absent';
    if (s === 'in') return 'In';
    if (s === 'completed') return 'Completed';
    return status;
  };

  const geofence = record?.officeValidation || null;
  const meetingModeOn = Boolean(record?.currentTracking?.meetingMode);

  return (
    <div className="attendance-shell">
      <div className="attendance-left">
        <div className="attendance-header">
          <button type="button" className="back-btn" onClick={() => window.history.back()}>
            <span className="material-symbols-outlined">arrow_back_ios</span>
          </button>
          <h2>Attendance Status</h2>
        </div>

        <div className="attendance-hero">
          <div className="hero-ring">
            <div className="hero-core">
              <span className="material-symbols-outlined">check</span>
            </div>
          </div>
          <h1>Daily Punch</h1>
          <p>Capture selfie and mark punch in / punch out</p>
        </div>

        <div className="attendance-card">
          {message && (
            <div className={`attendance-message ${message.includes('✅') || !message.includes('❌') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          {loading ? (
            <div className="attendance-loading"><Spinner animation="border" /></div>
          ) : employee ? (
            <Form>
              <div className="detail-group">
                <p>Employee</p>
                <h3>{employee.fullName}</h3>
              </div>

              <div className="detail-grid">
                <div>
                  <p>Status</p>
                  <div className="detail-value">{formatStatus(record?.status)}</div>
                </div>
                <div>
                  <p>Mobile</p>
                  <div className="detail-value">{employee.mobile}</div>
                </div>
              </div>

              <div className="detail-grid detail-grid-3">
                <div>
                  <p>Attendance Mode</p>
                  <div className="detail-value mode-tag">{activeMode === 'field' ? 'Field' : 'Office'}</div>
                </div>
                <div>
                  <p>Meeting</p>
                  <div className="detail-value">{meetingModeOn ? 'Running' : 'Not running'}</div>
                </div>
                <div>
                  <p>Office Range</p>
                  <div className="detail-value">{policyInfo?.policy?.officeRadiusMeters || 200} m</div>
                </div>
              </div>

              <div className="detail-row">
                <p>Location</p>
                <div className="detail-value">
                  {locationAddress || (location.lat && location.lng ? 'Location captured' : 'Location not detected')}
                </div>
              </div>
              {!!locationAddress && (
                <div className="detail-row">
                  <p>Current Address</p>
                  <div className="detail-value">{locationAddress}</div>
                </div>
              )}

              {!!policyInfo?.policy?.officeAddress && (
                <div className="detail-row">
                  <p>Office Address</p>
                  <div className="detail-value">{policyInfo.policy.officeAddress}</div>
                </div>
              )}

              {geofence && (
                <div className="detail-row">
                  <p>Office Validation</p>
                  <div className="detail-value">
                    {typeof geofence.distanceMeters === 'number'
                      ? `${geofence.distanceMeters} m from office`
                      : 'Distance unavailable'}
                    {typeof geofence.withinRange === 'boolean' ? ` | ${geofence.withinRange ? 'Within range' : 'Out of range'}` : ''}
                  </div>
                </div>
              )}

              <div className="detail-row">
                <p>Punch In</p>
                <div className="detail-value">
                  {record?.punchIn?.time ? new Date(record.punchIn.time).toLocaleString() : '--'}
                </div>
              </div>
              <div className="detail-row">
                <p>Punch Out</p>
                <div className="detail-value">
                  {record?.punchOut?.time ? new Date(record.punchOut.time).toLocaleString() : '--'}
                </div>
              </div>

              {activeMode === 'field' && (
                <>
                  <div className="detail-row">
                    <p>Field Note (optional)</p>
                    <Form.Control
                      value={meetingNote}
                      placeholder="Meeting/client/site note"
                      onChange={(e) => setMeetingNote(e.target.value)}
                    />
                  </div>
                  <div className="actions actions-grid-3">
                    <Button
                      variant="outline-primary"
                      type="button"
                      disabled={loading}
                      onClick={() => runFieldAction('/attendance/field/location', 'Location updated')}
                    >
                      Update Location
                    </Button>
                    {!meetingModeOn ? (
                      <Button
                        variant="warning"
                        type="button"
                        disabled={loading}
                        onClick={() => runFieldAction('/attendance/field/meeting/start', 'Meeting mode started')}
                      >
                        Start Meeting
                      </Button>
                    ) : (
                      <Button
                        variant="dark"
                        type="button"
                        disabled={loading}
                        onClick={() => runFieldAction('/attendance/field/meeting/end', 'Meeting mode ended')}
                      >
                        End Meeting
                      </Button>
                    )}
                  </div>
                </>
              )}

              {!isDesktop && (
                <div className="selfie-block">
                  <p>Live Selfie</p>
                  {capturedImage ? (
                    <>
                      <img src={capturedImage} alt="Captured" />
                      <Button variant="warning" onClick={() => setCapturedImage(null)}>Retake</Button>
                    </>
                  ) : (
                    <>
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        width="100%"
                        videoConstraints={{ facingMode: 'user' }}
                      />
                      <Button variant="primary" onClick={handleCapture}>
                        Capture Selfie
                      </Button>
                    </>
                  )}
                </div>
              )}

              <div className="actions">
                <Button variant="success" type="button" disabled={loading} onClick={handlePunchIn}>
                  {loading ? <Spinner size="sm" animation="border" /> : 'Punch In'}
                </Button>
                <Button variant="secondary" type="button" disabled={loading} onClick={handlePunchOut}>
                  {loading ? <Spinner size="sm" animation="border" /> : 'Punch Out'}
                </Button>
              </div>
            </Form>
          ) : (
            <div className="attendance-empty">Unable to load employee data.</div>
          )}
        </div>
      </div>

      {isDesktop && (
        <div className="attendance-right">
          <div className="attendance-photo-card">
            <div className="photo-title">Live Selfie</div>
            {capturedImage ? (
              <>
                <img src={capturedImage} alt="Captured" />
                <Button variant="warning" onClick={() => setCapturedImage(null)}>Retake</Button>
              </>
            ) : (
              <>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  width="100%"
                  videoConstraints={{ facingMode: 'user' }}
                />
                <Button variant="primary" onClick={handleCapture}>Capture Selfie</Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceForm;
